import { ArrayUtils, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../../jscaip/AI/GameNode';
import { CoordFailure } from '../../../jscaip/Coord';
import { GameStatus } from '../../../jscaip/GameStatus';
import { Ordinal } from '../../../jscaip/Ordinal';
import { ConfigurableRules } from '../../../jscaip/Rules';
import { RulesFailure } from '../../../jscaip/RulesFailure';
import { TableUtils } from '../../../jscaip/TableUtils';
import { Vector } from '../../../jscaip/Vector';
import { CheckersFailure } from './CheckersFailure';
import { CheckersMove } from './CheckersMove';
import { CheckersPiece, CheckersStack, EvenCheckersState, OddCheckersState } from './CheckersState';
export class CheckersOptionLocalizable {
    static STACK_PIECES = () => $localize `Stack pieces instead of capturing them`;
    static MAXIMAL_CAPTURE = () => $localize `You must capture the highest number of pieces`;
    static SIMPLE_PIECE_CAN_CAPTURE_BACKWARDS = () => $localize `Simple pieces can capture backward`;
    static PROMOTED_PIECES_CAN_TRAVEL_LONG_DISTANCES = () => $localize `Promoted pieces can travel long distance`;
    static OCCUPY_EVEN_SQUARE = () => $localize `Occupy upper-left corner`;
    static FRISIAN_CAPTURE_ALLOWED = () => $localize `Can do frisian captures`;
    static CAN_PROMOTE_MID_CAPTURE = () => $localize `Piece that reaches the last line during a capture continues capturing as king`;
}
export class CheckersNode extends GameNode {
}
export class AbstractCheckersRules extends ConfigurableRules {
    getInitialState(config) {
        const U = new CheckersStack([CheckersPiece.ZERO]);
        const V = new CheckersStack([CheckersPiece.ONE]);
        const _ = CheckersStack.EMPTY;
        const height = config.emptyRows + (2 * config.playerRows);
        const board = TableUtils.create(config.width, height, _);
        const occupiedSquare = config.occupyEvenSquare ? 0 : 1;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < config.width; x++) {
                if ((x + y) % 2 === occupiedSquare) {
                    if (y < config.playerRows) {
                        board[y][x] = V;
                    }
                    else if (config.playerRows + config.emptyRows <= y) {
                        board[y][x] = U;
                    }
                }
            }
        }
        if (config.occupyEvenSquare) {
            return EvenCheckersState.of(board, 0);
        }
        else {
            return OddCheckersState.of(board, 0);
        }
    }
    /**
     * @param state the state from which you want the current player's capture
     * @param config the config
     * @returns all the complete captures, whether or not they are legal
     */
    getCompleteCaptures(state, config) {
        const player = state.getCurrentPlayer();
        return this.getCapturesOf(state, player, config);
    }
    getCapturesOf(state, player, config) {
        const captures = [];
        const playerPieces = state.getStacksOf(player);
        for (const playerPiece of playerPieces) {
            captures.push(...this.getPieceCaptures(state, playerPiece, config, []));
        }
        return captures;
    }
    getPieceCaptures(state, coord, config, capturedCoords) {
        let pieceMoves = [];
        const piece = state.getPieceAt(coord);
        const pieceOwner = piece.getCommander().player;
        const opponent = pieceOwner.getOpponent();
        const directions = this.getPieceDirections(state, coord, true, config);
        for (const direction of directions) {
            const captured = this.getFirstCapturableCoord(state, coord, direction, opponent, capturedCoords, config);
            if (captured.isPresent()) {
                const landings = this.getLandableCoords(state, coord, captured.get(), direction, config);
                for (const landing of landings) {
                    const postCapture = this.applyCapture(state, coord, captured.get(), landing, config);
                    const startOfMove = CheckersMove.fromCapture([coord, landing]);
                    const newCapturedCoords = capturedCoords.concat(captured.get());
                    const endsOfMoves = this.getPieceCaptures(postCapture.state, landing, config, newCapturedCoords);
                    if (endsOfMoves.length === 0) {
                        pieceMoves = pieceMoves.concat(startOfMove);
                    }
                    else {
                        const mergedMoves = endsOfMoves.map((endMove) => {
                            return startOfMove.concatenate(endMove);
                        });
                        pieceMoves = pieceMoves.concat(mergedMoves);
                    }
                }
            }
        }
        return pieceMoves;
    }
    applyCapture(state, start, captured, landing, config) {
        const moved = state.getPieceAt(start);
        const pieceOwner = moved.getCommander().player;
        let fakePostCaptureState = state.remove(start);
        let landingPiece = moved;
        if (config.canStackPieces) {
            const capturedSpace = state.getPieceAt(captured);
            const remainingStack = capturedSpace.getPiecesUnderCommander();
            fakePostCaptureState = fakePostCaptureState.set(captured, remainingStack);
            landingPiece = moved.capturePiece(capturedSpace.getCommander());
        }
        if (config.canPromoteMidCapture && landing.y === state.getFinishLineOf(pieceOwner)) {
            landingPiece = landingPiece.promoteCommander();
        }
        fakePostCaptureState = fakePostCaptureState.set(landing, landingPiece);
        return { state: fakePostCaptureState, piece: landingPiece };
    }
    getFirstCapturableCoord(state, coord, direction, opponent, capturedCoords, config) {
        const isPromotedPiece = state.getPieceAt(coord).getCommander().isPromoted;
        if (config.promotedPiecesCanFly && isPromotedPiece) {
            return this.getFirstCapturableCoordForFlyingCapture(state, coord, direction, opponent, capturedCoords);
        }
        else {
            const nextCoord = coord.getNext(direction, 1);
            if (state.coordIsCommandedBy(nextCoord, opponent) &&
                this.isPresentIn(nextCoord, capturedCoords) === false) {
                return MGPOptional.of(nextCoord);
            }
            else {
                return MGPOptional.empty();
            }
        }
    }
    getLandableCoords(state, coord, captured, direction, config) {
        let possibleLanding = this.getNextPossibleLanding(state, captured, direction);
        const possibleLandings = [];
        if (possibleLanding.isPresent()) {
            possibleLandings.push(possibleLanding.get());
            const isPromotedPiece = state.getPieceAt(coord).getCommander().isPromoted;
            if (config.promotedPiecesCanFly && isPromotedPiece) {
                possibleLanding =
                    this.getNextPossibleLanding(state, possibleLanding.get(), direction);
                while (possibleLanding.isPresent()) {
                    possibleLandings.push(possibleLanding.get());
                    possibleLanding =
                        this.getNextPossibleLanding(state, possibleLanding.get(), direction);
                }
            }
        }
        return possibleLandings;
    }
    getNextPossibleLanding(state, coord, direction) {
        // A frisian capture can do even move but fly over even coord as well
        // so we must make a distinction between minimalisedDirection and direction
        const minimalisedDirection = direction.toMinimalVector();
        const nextPossibleLanding = coord.getNext(direction, 1);
        const distance = coord.getDistanceToward(nextPossibleLanding);
        let i = 0;
        while (i < distance) {
            coord = coord.getNext(minimalisedDirection, 1);
            if (state.isEmptyAt(coord) === false) {
                return MGPOptional.empty();
            }
            i++;
        }
        return MGPOptional.of(nextPossibleLanding);
    }
    isPresentIn(coord, coordList) {
        return coordList.some((c) => c.equals(coord));
    }
    getFirstCapturableCoordForFlyingCapture(state, coord, direction, opponent, capturedCoords) {
        const minimalisedDirection = direction.toMinimalVector();
        const nextCoord = coord.getNext(minimalisedDirection, 1);
        if (state.isNotOnBoard(nextCoord)) {
            return MGPOptional.empty();
        }
        if (state.getPieceAt(nextCoord).isEmpty()) {
            return this.getFirstCapturableCoordForFlyingCapture(state, nextCoord, minimalisedDirection, opponent, capturedCoords);
        }
        else if (state.getPieceAt(nextCoord).isCommandedBy(opponent)) {
            if (this.isPresentIn(nextCoord, capturedCoords)) {
                return MGPOptional.empty();
            }
            else {
                return MGPOptional.of(nextCoord);
            }
        }
        else {
            return MGPOptional.empty();
        }
    }
    getPieceDirections(state, coord, isCapture, config) {
        const piece = state.getPieceAt(coord);
        const pieceOwner = piece.getCommander().player;
        // Since player zero must go up and player one go down
        const verticalDirection = pieceOwner.getYDirection();
        const directions = [
            Ordinal.factory.fromDelta(-1, verticalDirection).get(), // left diagonal
            Ordinal.factory.fromDelta(1, verticalDirection).get(), // right diagonal
        ];
        if (isCapture && config.frisianCaptureAllowed) {
            const frisianForward = new Vector(0, 2 * verticalDirection);
            directions.push(new Vector(-2, 0), // left frisian
            new Vector(2, 0), // right frisian
            frisianForward);
            if (config.simplePieceCanCaptureBackwards) {
                const frisianBackward = new Vector(0, -2 * verticalDirection);
                directions.push(frisianBackward); // down frisian
            }
        }
        const isLegalCaptureBackward = isCapture && config.simplePieceCanCaptureBackwards;
        if (state.getPieceAt(coord).getCommander().isPromoted || isLegalCaptureBackward) {
            directions.push(Ordinal.factory.fromDelta(-1, -verticalDirection).get(), // down left diagonal
            Ordinal.factory.fromDelta(1, -verticalDirection).get());
        }
        return directions;
    }
    getSteps(state, config) {
        const player = state.getCurrentPlayer();
        return this.getStepsOf(state, player, config);
    }
    getStepsOf(state, player, config) {
        const steps = [];
        const playerStacks = state.getStacksOf(player);
        for (const playerPiece of playerStacks) {
            steps.push(...this.getPieceSteps(state, playerPiece, config));
        }
        return steps;
    }
    getPieceSteps(state, coord, config) {
        const pieceMoves = [];
        const directions = this.getPieceDirections(state, coord, false, config);
        for (const direction of directions) {
            const isPromotedPiece = state.getPieceAt(coord).getCommander().isPromoted;
            if (config.promotedPiecesCanFly && isPromotedPiece) {
                let landing = coord;
                let previousJumpWasPossible = true;
                while (previousJumpWasPossible) {
                    landing = landing.getNext(direction, 1);
                    previousJumpWasPossible = state.isEmptyAt(landing);
                    if (previousJumpWasPossible) {
                        const newStep = CheckersMove.fromStep(coord, landing);
                        pieceMoves.push(newStep);
                    }
                }
            }
            else {
                const landing = coord.getNext(direction, 1);
                if (state.isEmptyAt(landing)) {
                    const newStep = CheckersMove.fromStep(coord, landing);
                    pieceMoves.push(newStep);
                }
            }
        }
        return pieceMoves;
    }
    applyLegalMove(move, state, config) {
        return this.applyMove(move, state, config).incrementTurn();
    }
    applyMove(move, state, config) {
        const moveStart = move.getStartingCoord();
        const moveEnd = move.getEndingCoord();
        let movingStack = state.getPieceAt(moveStart);
        let resultingState = state.remove(moveStart);
        if (move.isStep === false) {
            const capturedCoords = [];
            for (let i = 1; i < move.coords.length; i++) {
                const previousCoord = move.coords[i - 1];
                const landingCoord = move.coords[i];
                const capturedCoord = this.getCapturedCoord(previousCoord, landingCoord, resultingState);
                if (capturedCoord.isPresent()) {
                    const captured = capturedCoord.get();
                    Utils.assert(this.isPresentIn(captured, capturedCoords) === false, 'A legal checkers move cannot capture the same coordinate twice.');
                    capturedCoords.push(captured);
                    const capturedSpace = resultingState.getPieceAt(captured);
                    const capturedCommander = capturedSpace.getCommander();
                    if (config.canStackPieces) {
                        movingStack = movingStack.capturePiece(capturedCommander);
                        const remainingStack = capturedSpace.getPiecesUnderCommander();
                        resultingState = resultingState.set(captured, remainingStack);
                    }
                    else {
                        resultingState = resultingState.set(captured, CheckersStack.EMPTY);
                    }
                }
                if (config.canPromoteMidCapture && landingCoord.y === state.getFinishLineOf(state.getCurrentPlayer())) {
                    movingStack = movingStack.promoteCommander();
                }
            }
        }
        resultingState = resultingState.set(moveEnd, movingStack);
        const finishLine = state.getFinishLineOf(state.getCurrentPlayer());
        const promotedMidCapture = config.canPromoteMidCapture &&
            move.coords.some((c) => c.y === finishLine);
        if (moveEnd.y === finishLine || promotedMidCapture) {
            resultingState = resultingState.set(moveEnd, movingStack.promoteCommander());
        }
        return resultingState;
    }
    getCapturedCoord(start, end, state) {
        const capturedCoords = start.getCoordsToward(end).filter((coord) => state.getPieceAt(coord).isOccupied());
        Utils.assert(capturedCoords.length <= 1, 'getCapturedCoord should only be called after single-capture validation.');
        if (capturedCoords.length === 1) {
            return MGPOptional.of(capturedCoords[0]);
        }
        else {
            return MGPOptional.empty();
        }
    }
    isLegal(move, state, config) {
        const moveOwnershipValidity = this.getMoveOwnershipValidity(move, state, config);
        if (moveOwnershipValidity.isFailure()) { // out of range, opponent, empty spaces
            return moveOwnershipValidity;
        }
        const moveValidity = this.isLegalSubMoveList(move, state, config);
        if (moveValidity.isFailure()) {
            return moveValidity;
        }
        const possibleCaptures = this.getCompleteCaptures(state, config);
        if (possibleCaptures.length === 0) {
            return MGPValidation.SUCCESS;
        }
        else {
            return this.isLegalCaptureChoice(move, state, possibleCaptures, config);
        }
    }
    getMoveOwnershipValidity(move, state, config) {
        const outOfRangeCoord = this.getMoveOutOfRangeCoord(move, config);
        if (outOfRangeCoord.isPresent()) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(outOfRangeCoord.get()));
        }
        const moveStart = move.getStartingCoord();
        if (state.getPieceAt(moveStart).isEmpty()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        const movedStack = state.getPieceAt(moveStart);
        const opponent = state.getCurrentOpponent();
        if (movedStack.isCommandedBy(opponent)) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        return MGPValidation.SUCCESS;
    }
    getMoveOutOfRangeCoord(move, config) {
        const configHeight = config.emptyRows + (2 * config.playerRows);
        for (const coord of move.coords) {
            if (coord.isNotInRange(config.width, configHeight)) {
                return MGPOptional.of(coord);
            }
        }
        return MGPOptional.empty();
    }
    isLegalSubMoveList(move, state, config) {
        let stack = state.getPieceAt(move.coords[0]);
        const isSimpleJump = move.coords.length === 2;
        let validationState = state.remove(move.coords[0]);
        const capturedCoords = [];
        const finishLine = state.getFinishLineOf(state.getCurrentPlayer());
        for (let i = 1; i < move.coords.length; i++) {
            const previousCoord = move.coords[i - 1];
            const landingCoord = move.coords[i];
            const subMoveValidity = this.getSubMoveValidity(stack, isSimpleJump, previousCoord, landingCoord, validationState, config);
            if (subMoveValidity.isFailure()) {
                return subMoveValidity;
            }
            const capturedCoord = this.getCapturedCoord(previousCoord, landingCoord, validationState);
            if (capturedCoord.isPresent()) {
                const captured = capturedCoord.get();
                if (this.isPresentIn(captured, capturedCoords)) {
                    return MGPValidation.failure(CheckersFailure.CANNOT_CAPTURE_TWICE_THE_SAME_SQUARE());
                }
                capturedCoords.push(captured);
                if (config.canStackPieces) {
                    const capturedStack = validationState.getPieceAt(captured);
                    stack = stack.capturePiece(capturedStack.getCommander());
                    validationState =
                        validationState.set(captured, capturedStack.getPiecesUnderCommander());
                }
            }
            if (config.canPromoteMidCapture && landingCoord.y === finishLine) {
                stack = stack.promoteCommander();
            }
        }
        return MGPValidation.SUCCESS;
    }
    getSubMoveValidity(stack, isSimpleJump, start, end, state, config) {
        const landingPiece = state.getPieceAt(end);
        if (landingPiece.getStackSize() > 0) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        const directionValidity = this.getDirectionValidity(start, end, config);
        if (directionValidity.isFailure()) {
            return directionValidity;
        }
        const flownOverPlayers = this.getFlownOverPlayers(start, end, state);
        let isCapture;
        if (flownOverPlayers.length === 0) { // No Capture
            if (isSimpleJump) {
                // This is a simple jump without capture, must be a promoted piece (this part is checked later)
                isCapture = false;
            }
            else {
                // This is a sequence of capture that finishes with a non capture
                return MGPValidation.failure(CheckersFailure.MOVE_CANNOT_CONTINUE_AFTER_NON_CAPTURE_MOVE());
            }
        }
        else if (flownOverPlayers.length > 1) { // Capturing 2+ pieces
            return MGPValidation.failure(CheckersFailure.CANNOT_JUMP_OVER_SEVERAL_PIECES());
        }
        else { // Single Capture
            if (flownOverPlayers.some((player) => player.equals(state.getCurrentPlayer()))) {
                return MGPValidation.failure(RulesFailure.CANNOT_SELF_CAPTURE());
            }
            isCapture = true;
        }
        if (this.isNormalPieceGoingBackwardIllegaly(stack, start, end, state, config)) {
            return MGPValidation.failure(CheckersFailure.ONLY_PROMOTED_PIECES_CAN_GO_BACKWARD());
        }
        const flyLegality = this.getFlyLegality(stack, start, end, isCapture, config);
        if (flyLegality.isFailure()) {
            return flyLegality;
        }
        return MGPValidation.SUCCESS;
    }
    getDirectionValidity(start, end, config) {
        const direction = start.getDirectionToward(end);
        if (direction.isFailure()) {
            return MGPValidation.failure(direction.getReason());
        }
        if (direction.get().isOrthogonal()) {
            if (config.frisianCaptureAllowed) {
                const frisianSize = start.getDistanceToward(end);
                if (frisianSize % 2 === 1) {
                    return MGPValidation.failure(CheckersFailure.FRISIAN_CAPTURE_MUST_BE_EVEN());
                }
                else if (frisianSize === 2) {
                    return MGPValidation.failure(CheckersFailure.INVALID_FRISIAN_MOVE());
                }
            }
            else {
                return MGPValidation.failure(CheckersFailure.CANNOT_MOVE_ORTHOGONALLY());
            }
        }
        return MGPValidation.SUCCESS;
    }
    getFlownOverPlayers(start, end, state) {
        const fliedOverCoords = start.getCoordsToward(end);
        const fliedOverPieces = fliedOverCoords.map((coord) => state.getPieceAt(coord));
        const fliedOverOccupiedStacks = fliedOverPieces.filter((stack) => stack.isOccupied());
        return fliedOverOccupiedStacks.map((stack) => stack.getCommander().player);
    }
    /**
     * @param stepStart the start of the step
     * @param stepEnd the end of the step
     * @param state the state before the step
     * @param config the config
     */
    isNormalPieceGoingBackwardIllegaly(piece, stepStart, stepEnd, state, config) {
        const commander = piece.getCommander();
        if (commander.isPromoted) {
            return false;
        } // Here: we check a normal piece
        const opponent = state.getCurrentOpponent();
        const moveDirection = stepStart.getDirectionToward(stepEnd).get().y;
        const distance = stepStart.getDistanceToward(stepEnd);
        const isBackward = moveDirection === opponent.getYDirection();
        if (isBackward) {
            if (distance === 1) {
                return true; // Piece is stepping backward illegally
            }
            else if (config.simplePieceCanCaptureBackwards === false) {
                return true; // Piece is backcapturing illegally
            }
            else {
                return false; // Legal backward
            }
        }
        else {
            return false; // Legal forward
        }
    }
    getFlyLegality(stack, stepStart, stepEnd, isCapture, config) {
        const distance = stepStart.getDistanceToward(stepEnd);
        if (isCapture) {
            if (distance === 2) {
                return MGPValidation.SUCCESS;
            }
            if (distance === 4 && config.frisianCaptureAllowed) {
                return MGPValidation.SUCCESS;
            }
        }
        else {
            if (distance === 1) {
                return MGPValidation.SUCCESS;
            }
        }
        if (config.promotedPiecesCanFly) {
            if (stack.getCommander().isPromoted) {
                return MGPValidation.SUCCESS; // Legal promoted fly
            }
            else { // Normal piece cannot fly
                if (isCapture) {
                    return MGPValidation.failure(CheckersFailure.FLYING_CAPTURE_IS_FORBIDDEN_FOR_NORMAL_PIECES());
                }
                else {
                    return MGPValidation.failure(CheckersFailure.NORMAL_PIECES_CANNOT_MOVE_LIKE_THIS());
                }
            }
        }
        else { // No piece are allowed to fly
            return MGPValidation.failure(CheckersFailure.NO_PIECE_CAN_DO_LONG_JUMP());
        }
    }
    /**
    * @param move the chosen capture
    * @param possibleCaptures all possible captures
    * @param config the config
    * @returns whether or not this move is amongst the possible capture, based on global-capture group
    * The check aspect are only based on the rules mustMakeMaximalCapture and partialCapture rules
    */
    isLegalCaptureChoice(move, state, possibleCaptures, config) {
        if (move.isStep) {
            return MGPValidation.failure(CheckersFailure.CANNOT_SKIP_CAPTURE());
        }
        if (config.mustMakeMaximalCapture) {
            const legalCaptures = ArrayUtils.maximumsBy(possibleCaptures, (m) => m.coords.length);
            const captureSize = move.coords.length;
            const awaitedCaptureSize = legalCaptures[0].coords.length;
            if (captureSize === awaitedCaptureSize) {
                return MGPValidation.SUCCESS;
            }
            else if (legalCaptures.some((m) => move.isPrefix(m))) {
                return MGPValidation.failure(CheckersFailure.MUST_FINISH_CAPTURING());
            }
            else {
                Utils.assert(awaitedCaptureSize > captureSize, 'capture is longer than it should!');
                return MGPValidation.failure(CheckersFailure.MUST_DO_LONGEST_CAPTURE());
            }
        }
        else {
            if (possibleCaptures.some((m) => m.equals(move))) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(CheckersFailure.MUST_FINISH_CAPTURING());
            }
        }
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const captures = this.getCompleteCaptures(state, config);
        if (captures.length > 0 || this.getSteps(state, config).length > 0) {
            return GameStatus.ONGOING;
        }
        else {
            return GameStatus.getVictory(state.getCurrentOpponent());
        }
    }
}
